<script setup lang="ts">
    import { DEFAULT_IMG } from '@/constants/image';
    import formKitConfig from '@/formkit';
    import { formatCurrency } from '@/helpers/formatting';
    import { type GuestTypePriceTyping, type ProductTyping } from '@/typings/widget';
    import { FormKitProvider, FormKitRoot, defaultConfig } from '@formkit/vue';
    import Checkbox from '@shared/components/form/checkbox.vue';
    import AdvancedImage from '@shared/components/ui/advanced-image.vue';
    import { ProductPricingStrategy } from '@shared/constants/payments';

    const props = defineProps<{
        item: ProductTyping
        selected?: boolean
        showPrice?: boolean
    }>();

    const { t } = useI18n();

    const selectedModel = ref(props.selected ?? false);

    const unitPriceFilteredAmounts: ComputedRef<GuestTypePriceTyping[] | undefined> = computed(() =>
        props.item.guestTypePrices?.filter(price =>
            props.item.guestTypes.some(guestType =>
                price.guestType.id === guestType.id)) ?? []);

    watch(() => props.selected, nVal => {
        selectedModel.value = nVal;
    });
</script>

<template>
    <div
        :class="{
            'quote-card--selected': selected,
        }"
        class="quote-card"
    >
        <div
            :class="{
                'quote-card__image--selected': selected,
            }"
            class="quote-card__image"
        >
            <AdvancedImage
                v-if="item.cover"
                :media="item.cover"
                :asset-width="850"
                no-crop
                aspect-ratio="1:1"
            />
            <img
                v-else
                :src="DEFAULT_IMG"
            >
        </div>
        <div class="quote-card__title">
            <div class="quote-card__title__content">
                <strong>
                    {{ item.name }}
                </strong>
            </div>
        </div>
        <div
            v-if="showPrice"
            class="quote-card__prices"
        >
            <template v-if="item.pricingStrategy === ProductPricingStrategy.PerGuesttype">
                <div
                    v-for="guestType in unitPriceFilteredAmounts"
                    :key="`${guestType?.id}-${guestType.guestType.abbreviation}`"
                    class="quote-card__price"
                >
                    <strong>
                        {{ formatCurrency(guestType.price ?? 0) }}
                    </strong>
                    {{ guestType.guestType.abbreviation }}
                </div>
            </template>
            <div
                v-else
                class="quote-card__price"
            >
                <strong>
                    {{ formatCurrency(item.price) }}
                </strong>
                {{ t(`widget.global.pricing-strategy.${item.pricingStrategy}`) }}
            </div>
        </div>
        <div class="quote-card__checkbox">
            <FormKitProvider :config="defaultConfig(formKitConfig)">
                <FormKitRoot>
                    <Checkbox
                        v-model="selectedModel"
                        name="selected"
                        readonly
                        @click.prevent
                    />
                </FormKitRoot>
            </FormKitProvider>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.quote-card {
    position: relative;
    display: flex;
    overflow: hidden;
    flex-direction: column;
    justify-content: space-between;
    border: 1px solid transparent;
    border-radius: var(--border-radius-md);
    cursor: pointer;
    inline-size: 100%;
    max-inline-size: 100%;
    transition: all 0.2s ease;

    @include max-width(lg) {
        border: 1px solid var(--border-secondary);
    }

    &:hover:not(.quote-card--selected) {
        border-color: var(--border-tertiary);
        box-shadow: 0 0 5px 0 rgb(0 0 0 / 0.1);
        transform: translateY(-2px);
    }

    &--selected {
        border: 1px solid var(--border-primary);
    }

    &__image {
        overflow: hidden;
        border-radius: var(--border-radius-md);
        aspect-ratio: 1 / 1;
        block-size: 100%;
        inline-size: 100%;
    }

    &__title,
    &__prices,
    &__checkbox {
        position: absolute;
    }

    &__title {
        inline-size: 100%;
        inset-block-end: 0;

        &__content {
            padding: var(--spacing-02);
            background: var(--background-secondary-highlight);
            font-size: var(--font-regular);
            text-align: center;

            @include min-width(lg) {
                padding: var(--spacing-03);
            }
        }
    }

    &__checkbox {
        inset-block-start: var(--spacing-02);
        inset-inline-end: var(--spacing-02);

        @include min-width(lg) {
            inset-block-start: var(--spacing-03);
            inset-inline-end: var(--spacing-03);
        }
    }

    &__prices {
        display: flex;
        flex-direction: column;
        padding: var(--spacing-02);
        border-radius: var(--border-radius-sm);
        background: var(--background-secondary-highlight);
        inset-block-start: var(--spacing-02);
        inset-inline-start: var(--spacing-02);

        @include min-width(lg) {
            gap: var(--spacing-01);
            inset-block-start: var(--spacing-03);
            inset-inline-start: var(--spacing-03);
        }
    }

    &__price {
        color: var(--text-secondary);
        font-size: var(--font-small);
    }
}
</style>
